import { useEffect, useRef, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import orderServices from "../../services/orders/orderservice";
import { POSModel } from "../../models/orders/order-list-model";
import posService from "../../services/pos/pos.service";

interface PosMovementSearchProps {
  setLoading: (loading: boolean) => void;
  onSearch: (data: any[]) => void;
  onSummary: (data: any[]) => void;
  setPosId: (posId: number) => void;
  setFromDate: (startDate: any) => void;
  setToDate: (endDate: any) => void;
  onKeywordChange: (keyword: string) => void;
  onResetFilters?: () => void;
}

const PosMovementSearch: React.FC<PosMovementSearchProps> = ({
  setLoading,
  onSearch,
  onSummary,
  setPosId,
  setFromDate,
  setToDate,
  onKeywordChange,
  onResetFilters
}) => {
  const toast = useRef<Toast>(null);


  const [posList, setPosList] = useState<POSModel[]>([]);
  const [selectedPosId, setSelectedPosId] = useState<number | null>(null);
  // const today = new Date();
  // const yesterday = new Date(today);
  // yesterday.setDate(today.getDate() - 1);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    const fetchPOS = async () => {
      try {
        const response = await orderServices.getPOS();
        setPosList(response);
        if (response.length === 1) {
          const singlePOSId = response[0].posID;
          setSelectedPosId(singlePOSId);
        }
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load POS data.",
        });
      }
    };
    fetchPOS();
  }, []);


  const handleSearch = async () => {
    if (!selectedPosId) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the POS",
      });
      return;
    }

    if (!startDate && !endDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Start date and End date",
      });
      return;
    }

    if (!startDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Start date",
      });
      return;
    }

    if (!endDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the End date",
      });
      return;
    }

    setLoading(true);
    // Do filtering logic here
    const adjustedStartDate = new Date(startDate);
    adjustedStartDate.setHours(0, 0, 0, 0);

    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);

    const formatToSQLDateTime = (date: Date) =>
      date.toLocaleString("sv-SE").replace(" ", "T");

    const payload = {
      posId: selectedPosId,
      startDate: formatToSQLDateTime(adjustedStartDate),
      endDate: formatToSQLDateTime(adjustedEndDate),
    };


    try {
      const result = await posService.GetPOSMovements(payload);
      if (result?.movemementresult) {
        setPosId(selectedPosId);
        setFromDate(formatToSQLDateTime(adjustedStartDate));
        setToDate(formatToSQLDateTime(adjustedEndDate));
        onSearch(result.movemementresult);
      } else {
        toast.current?.show({
          severity: "info",
          summary: "No data",
          detail: "No movement records found.",
        });
        onSearch([]);
      }
      // Call Summary API
      const Result1 = await posService.GetPOSSummary(payload);
      if (Result1?.summaryresult) {
        Result1.summaryresult.startDate = startDate;
        Result1.summaryresult.endDate = endDate;
        Result1.summaryresult.pOSId = selectedPosId;
        onSummary(Result1.summaryresult);
      } else {
        onSummary([]);
      }

    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch movement logs or summary.",
      });
      onSearch([]);
      onSummary([]);
    } finally {
      setLoading(false);
    }

  };

  const handleReset = () => {
    if (posList.length === 1) {
      setSelectedPosId(posList[0].posID); // Auto-select if only 1 POS
    } else {
      setSelectedPosId(null); // Clear selection if multiple
    }
    setPosId(0);
    setStartDate(null);
    setFromDate(null);
    setEndDate(null);
    setToDate(null);
    setKeyword("");
    onKeywordChange("");
    onSearch([]);
    onSummary([]);

    // Reset table filters
    onResetFilters?.();
  };

  //   useEffect(() => {
  //   if (selectedPosId && startDate && endDate) {
  //     handleSearch();
  //   }
  // }, [selectedPosId, startDate, endDate]);

  return (
    <div className="card p-4 w-full" style={{
      backgroundColor: '#e8f5e9',
      borderRadius: '12px'
    }}>
      <Toast ref={toast} />

      {/* Header Section */}
      <div className="flex align-items-center gap-3 mb-4" style={{
        backgroundImage: 'linear-gradient(to right, white, rgb(232, 245, 233))',
        padding: '16px',
        borderRadius: '8px',
        marginLeft: '-16px',
        marginRight: '-16px',
        marginTop: '-16px',
        paddingLeft: '16px',
        paddingRight: '16px'
      }}>
        <div style={{
          backgroundColor: 'rgb(76, 175, 80)',
          borderRadius: '10px',
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className="pi pi-sync" style={{ color: 'white', fontSize: '1.5rem' }}></i>
        </div>
        <div>
          <h5 className="m-0 mb-1" style={{ color: '#2d3748', fontWeight: '600' }}>
            POS Movements
          </h5>
          <p className="m-0" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Track, manage, and view POS inventory movements
          </p>
        </div>
      </div>

      {/* Filter Header */}
      <div className="flex align-items-center gap-2 mb-3">
        <i className="pi pi-filter" style={{ color: 'rgb(76, 175, 80)', fontSize: '1.1rem' }}></i>
        <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>Filter Movements</span>
      </div>

      <div className="grid">
        {/* POS Dropdown */}
        <div className="col-12 md:col-2">
          <Dropdown
            value={selectedPosId}
            options={posList}
            optionLabel="posCode"
            optionValue="posID"
            placeholder="Select POS"
            onChange={(e: DropdownChangeEvent) => setSelectedPosId(e.value)}
            className="w-full custom-rounded-dropdown"
            disabled={posList.length === 1}
            pt={{
              root: { style: { borderRadius: '8px' } },
              input: { style: { borderRadius: '8px' } }
            }}
          />
        </div>

        {/* Start Date */}
        <div className="col-6 md:col-2">
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate(e.value as Date | null)}
            showButtonBar
            dateFormat="dd/mm/yy"
            placeholder="Start Date"
            className="w-full"
            readOnlyInput
            inputStyle={{ borderRadius: '8px' }}
          />
        </div>

        {/* End Date */}
        <div className="col-6 md:col-2">
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate(e.value as Date | null)}
            showButtonBar
            dateFormat="dd/mm/yy"
            placeholder="End Date"
            className="w-full"
            readOnlyInput
            inputStyle={{ borderRadius: '8px' }}
          />
        </div>

        {/* Keyword Search */}
        <div className="col-6 md:col-2">
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                onKeywordChange(e.target.value);
              }}
              placeholder="Search..."
              className="w-full"
              style={{ borderRadius: '8px' }}
            />
          </span>
        </div>


        {/* Buttons */}
        <div className="col-6 md:col-2">
          <Button
            label="Filter"
            onClick={handleSearch}
            severity="success"
            className="w-full"
            style={{ borderRadius: '8px' }}
          />
        </div>
        <div className="col-6 md:col-2">
          <Button
            label="Reset"
            onClick={handleReset}
            severity="secondary"
            className="w-full"
            text
            raised
            style={{ borderRadius: '8px' }}
          />
        </div>
      </div>
    </div>
  );
};

export default PosMovementSearch;